"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.AsyncTable = void 0;
var _react = _interopRequireWildcard(require("react"));
var _useInfiniteScroll2 = require("./useInfiniteScroll");
var _useDebounce = require("../../lib/hooks/useDebounce");
var _Grid = require("./Grid");
var _Table = require("./Table");
var _Controls = require("./Controls");
var _LoadingIndicator = require("../LoadingIndicator");
var _AsyncTableModule = _interopRequireDefault(require("./AsyncTable.module.css"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
var CONFIG = {
  chunkSizeGrid: 6,
  chunkSizeList: 10,
  searchDebounceMilliseconds: 100
};

/**
 * NOTE:
 * This table should only be used when data is coming
 * from multiple sources, but we need to defer loading
 * after the first source.
 *
 * For example, when loading a zNS NFT, the initial query
 * grabs a list of domain IDs and metadata URLs. We might
 * want to also show the name of the NFT and how many bids
 * have been placed on it. Both of these data points come
 * from different sources. Loading of these resources
 * should be deferred to a row component, and the minimal
 * data for the row (i.e. data from the first query) should
 * be rendered first.
 */
var AsyncTable = _ref => {
  var className = _ref.className,
    columns = _ref.columns,
    data = _ref.data,
    gridComponent = _ref.gridComponent,
    isLoading = _ref.isLoading,
    _ref$isSingleColumnGr = _ref.isSingleColumnGrid,
    isSingleColumnGrid = _ref$isSingleColumnGr === void 0 ? false : _ref$isSingleColumnGr,
    loadingText = _ref.loadingText,
    emptyText = _ref.emptyText,
    rowComponent = _ref.rowComponent,
    searchKey = _ref.searchKey,
    _ref$showControls = _ref.showControls,
    showControls = _ref$showControls === void 0 ? true : _ref$showControls,
    _ref$isGridViewByDefa = _ref.isGridViewByDefault,
    isGridViewByDefault = _ref$isGridViewByDefa === void 0 ? true : _ref$isGridViewByDefa;
  var lastView = (0, _react.useRef)();
  var _useState = (0, _react.useState)(isGridViewByDefault),
    _useState2 = _slicedToArray(_useState, 2),
    isGridView = _useState2[0],
    setIsGridView = _useState2[1];
  var _useState3 = (0, _react.useState)(''),
    _useState4 = _slicedToArray(_useState3, 2),
    searchQuery = _useState4[0],
    setSearchQuery = _useState4[1];
  var query = (0, _useDebounce.useDebounce)(searchQuery, CONFIG.searchDebounceMilliseconds);

  // Filter data based on search query
  var filteredData = (0, _react.useMemo)(() => {
    if (!query || !searchKey) {
      return data;
    }
    return data === null || data === void 0 ? void 0 : data.filter(item => {
      var _item$searchKey$key;
      var searchValue = (_item$searchKey$key = item[searchKey.key]) === null || _item$searchKey$key === void 0 ? void 0 : _item$searchKey$key.toString().toLowerCase();
      return searchValue.includes(query.toLowerCase());
    });
  }, [data, query, searchKey]);

  // Set up infinite scroll chunks
  var _useInfiniteScroll = (0, _useInfiniteScroll2.useInfiniteScroll)({
      items: filteredData !== null && filteredData !== void 0 ? filteredData : [],
      chunkSize: isGridView ? CONFIG.chunkSizeGrid : CONFIG.chunkSizeList,
      component: isGridView ? gridComponent : rowComponent
    }),
    InfiniteScrollWrapper = _useInfiniteScroll.InfiniteScrollWrapper,
    chunkedComponents = _useInfiniteScroll.chunkedComponents,
    resetChunk = _useInfiniteScroll.resetChunk;

  // Warn if dev didn't memoize rowComponent
  (0, _react.useEffect)(() => {
    if (lastView.current === 'list' && !isGridView) {
      console.warn('Detected unmemoized rowComponent!');
    }
  }, [rowComponent]);

  // Warn if dev didn't memoize gridComponent
  (0, _react.useEffect)(() => {
    if (lastView.current === 'grid' && isGridView) {
      console.warn('Detected unmemoized gridComponent!');
    }
  }, [gridComponent]);

  // Reset infinite scroll whenever user changes view
  (0, _react.useEffect)(() => {
    lastView.current = 'grid';
    resetChunk();
  }, [isGridView, resetChunk]);

  // Grid view doesn't render Skeletons when loading (yet)
  if (isGridView && isLoading) {
    return /*#__PURE__*/_react.default.createElement(_LoadingIndicator.LoadingIndicator, {
      className: _AsyncTableModule.default.Loading,
      text: loadingText !== null && loadingText !== void 0 ? loadingText : 'Loading'
    });
  }
  var isEmpty = !query && !isLoading && !(data !== null && data !== void 0 && data.length);
  var isSearchEmpty = query && !isLoading && !filteredData.length;

  // Show emptyText when there is no data
  if (isEmpty) {
    return /*#__PURE__*/_react.default.createElement("p", {
      className: _AsyncTableModule.default.Empty
    }, emptyText !== null && emptyText !== void 0 ? emptyText : 'No items to display.');
  }
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, showControls && /*#__PURE__*/_react.default.createElement(_Controls.Controls, {
    canSwitchViews: Boolean(gridComponent) && Boolean(rowComponent),
    onSwitchViews: isGridView => setIsGridView(isGridView),
    isGridView: isGridView,
    searchPlaceholder: (searchKey === null || searchKey === void 0 ? void 0 : searchKey.name) && "Search by ".concat(searchKey === null || searchKey === void 0 ? void 0 : searchKey.name),
    searchQuery: searchQuery,
    onChangeSearchQuery: setSearchQuery,
    isSearchable: Boolean(searchKey)
  }), /*#__PURE__*/_react.default.createElement(InfiniteScrollWrapper, {
    className: className
  }, isGridView ? /*#__PURE__*/_react.default.createElement(_Grid.Grid, {
    cards: chunkedComponents,
    isSingleColumnGrid: isSingleColumnGrid
  }) : /*#__PURE__*/_react.default.createElement(_Table.Table, {
    rows: chunkedComponents,
    columns: columns,
    isLoading: isLoading
  })), isSearchEmpty && /*#__PURE__*/_react.default.createElement("p", {
    className: _AsyncTableModule.default.Empty
  }, "No items match your search ", /*#__PURE__*/_react.default.createElement("strong", null, query), "."));
};
exports.AsyncTable = AsyncTable;
//# sourceMappingURL=AsyncTable.js.map